import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import API_ENDPOINTS from "../../config/config";
import "./dashboard.css";

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    is_admin: false,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const userInfo = JSON.parse(localStorage.getItem("user"));
  

  const BASE = API_ENDPOINTS.BASE;

  // Add this useEffect to handle clicking outside to close popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".user-profile-icon")) {
        setShowUserInfo(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    // Check for token and admin status
    const token = localStorage.getItem("token");
    const userIsAdmin = localStorage.getItem("isAdmin") === "true";

    if (!token) {
      navigate("/login");
      return;
    }

    setIsAdmin(userIsAdmin);
  }, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Add keyboard support for modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
        setError("");
        setSuccess("");
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent background scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");
      // Ensure is_admin is always a boolean
      const modifiedFormData = {
        ...formData,
        is_admin: Boolean(formData.is_admin), // Convert to boolean
      };

      const response = await fetch(`${BASE}/add-user.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(modifiedFormData),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess("User added successfully!");
        setFormData({
          email: "",
          password: "",
          is_admin: false,
        });
        setTimeout(() => {
          setIsModalOpen(false);
          setSuccess("");
        }, 2000);
      } else {
        setError(data.message || "Failed to add user");
      }
    } catch (err) {
      setError("An error occurred while adding the user");
      if (err.message === "Unauthorized") {
        navigate("/login");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: value,
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("isAdmin");
    navigate("/login");
  };

  if (
    location.pathname === "/login" ||
    location.pathname === "/" ||
    location.pathname === "/forgot-password"
  ) {
    return null;
  }

  return (
    <div className="min-h-screen">
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-left">
            {isAdmin && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="add-user-btn"
                title="Add User"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" y1="8" x2="19" y2="14" />
                  <line x1="16" y1="11" x2="22" y2="11" />
                </svg>
              </button>
            )}
            <div className="navbar-brand">
              <img 
                src="/fantasy.jpeg" 
                alt="FPPL" 
                className="navbar-logo"
              />
              <span className="brand-text">Fantasy Packaging</span>
            </div>
          </div>

          <div className="navbar-right">
            <Link
              to="/logout"
              className="logout-btn"
              title="Logout"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </Link>

            <div
              className="user-profile-icon"
              onClick={(e) => {
                e.stopPropagation();
                setShowUserInfo(!showUserInfo);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>

              {showUserInfo && (
                <div className="user-info-popup">
                  <div className="email">{userInfo?.email || "User"}</div>
                  <div className="role">
                    {userInfo?.isAdmin ? "Administrator" : "Standard User"}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {isModalOpen && isAdmin && (
        <div 
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
              setError("");
              setSuccess("");
            }
          }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-header-content">
                <h2>Add New User</h2>
              </div>
              <button
                className="close-button"
                aria-label="Close modal"
                onClick={() => {
                  setIsModalOpen(false);
                  setError("");
                  setSuccess("");
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="error-message">
                  <div className="message-icon">⚠️</div>
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="success-message">
                  <div className="message-icon">✅</div>
                  <span>{success}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="add-user-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter user email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter secure password"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <div className="checkbox-wrapper">
                    <input
                      id="is_admin"
                      name="is_admin"
                      type="checkbox"
                      checked={formData.is_admin}
                      onChange={handleChange}
                    />
                    <label htmlFor="is_admin">Grant Administrator Rights</label>
                  </div>
                  <div className="checkbox-description">
                    Administrators can add users and access all features
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="submit-button"
                  >
                    {isLoading ? (
                      <>
                        <div className="loading-spinner"></div>
                        Adding...
                      </>
                    ) : (
                      "Add User"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="main-content">
        <div className="date-display">
          <div>
            {currentTime.toLocaleString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            })}
          </div>
        </div>
        <h1 className="main-title">Fantasy Packaging Private Limited</h1>
        <div className="buttons-grid">
          <Link to="/paper-raw" className="dashboard-card">
            <div className="card-icon">📄</div>
            <div className="card-title">Paper Raw Material</div>
            <div className="card-description">Manage paper inventory and supplies</div>
          </Link>
          <Link to="/polythene-raw" className="dashboard-card">
            <div className="card-icon">♻️</div>
            <div className="card-title">Polythene Raw Material</div>
            <div className="card-description">Track polythene materials and stock</div>
          </Link>
          <Link to="/store" className="dashboard-card">
            <div className="card-icon">🏪</div>
            <div className="card-title">Store</div>
            <div className="card-description">Access store management tools</div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
